/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { decomposeColor, lighten } from "@mui/material";
import { useReducedMotion } from "framer-motion";
import type { JSX } from "react";

import { MotionDiv } from "./lib";

interface ShineOverlayProps {
  color: string;
}

/**
 * Overlay that renders a subtle shine moving from right to left
 * across a positioned container.
 * The parent element must have `position: relative` and `overflow: hidden`.
 */
export function ShineOverlay({ color }: ShineOverlayProps): JSX.Element | null {
  const prefersReducedMotion = useReducedMotion();

  if (prefersReducedMotion) {
    return null;
  }

  const lightColor = lighten(color, 0.9);
  const [r, g, b] = decomposeColor(lightColor).values;

  return (
    <div
      css={css`
        position: absolute;
        inset: 0;
        pointer-events: none;
        overflow: hidden;
      `}
    >
      <MotionDiv
        css={css`
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            100deg,
            rgba(${r}, ${g}, ${b}, 0) 0%,
            rgba(${r}, ${g}, ${b}, 0.07) 25%,
            rgba(${r}, ${g}, ${b}, 0.3) 45%,
            rgba(${r}, ${g}, ${b}, 0.07) 65%,
            rgba(${r}, ${g}, ${b}, 0) 100%
          );
        `}
        animate={{ x: ["100%", "-100%"] }}
        transition={{
          duration: 4,
          ease: "linear",
          repeat: Infinity,
        }}
      />
    </div>
  );
}
